import { useMutation } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { toastFactory } from '@/factories'
import { ChecklistStatus } from '@/models/features'
import { resetQueries } from '@/utils'

import { GET_CHECKLIST_TASK_DETAILS_BASE_QUERY_KEY } from './get-checklist-task-details'
import { GET_CHECKLISTS_BASE_QUERY_KEY } from './get-checklists'

export type ChecklistBulkStatus = Extract<ChecklistStatus, 'completed' | 'not-started'>

const BULK_STATUS_PAYLOAD_MAP: Record<ChecklistBulkStatus, string> = {
  completed: 'Completed',
  'not-started': 'Not Started',
}

interface BulkUpdateStatusChecklistsRequest {
  checklistIds: number[]
  buildingId: number
  status: ChecklistBulkStatus
}

interface BulkUpdateStatusChecklistsResponse {
  completed: number[]
  failed: {
    id: number
    status: number
    message: string
  }[]
}

export const bulkUpdateStatusChecklists = async (data: BulkUpdateStatusChecklistsRequest) => {
  const response = await authAPI.post<BulkUpdateStatusChecklistsResponse>(
    `/checklist-unified/${data.buildingId}/bulk-status`,
    {
      ids: data.checklistIds,
      status: BULK_STATUS_PAYLOAD_MAP[data.status],
    },
  )
  return response.data
}

export const useBulkUpdateStatusChecklists = () => {
  return useMutation({
    mutationFn: bulkUpdateStatusChecklists,
    onError: () => {
      toastFactory.error("Couldn't update status. Please try again.")
    },
    onSuccess: async (response) => {
      await resetQueries(
        [GET_CHECKLISTS_BASE_QUERY_KEY],
        [GET_CHECKLIST_TASK_DETAILS_BASE_QUERY_KEY],
      )

      if (response.failed.length > 0) {
        // Note: Backend rejects inspections with 400 ("Only tasks can be updated.")
        const hasFailedInspections = response.failed.some((item) => item.status === 400)

        if (hasFailedInspections) {
          toastFactory.success(
            `${response.completed.length} updated. Inspections can't be updated in bulk — please update them from the inspection details page.`,
          )
          return
        }

        toastFactory.error(
          `${response.completed.length} updated, ${response.failed.length} couldn't be updated. Please try again.`,
        )
        return
      }

      toastFactory.success('Status updated successfully.')
    },
  })
}
